import React from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, Mail, MapPin, Clock, Globe } from 'lucide-react';
import { Facebook, Instagram, Twitter, Youtube, MessageCircle } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { BRAND } from '../../data/brand';

export default function ContactInfo() {
  const { t } = useTranslation();
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return <Facebook className="h-5 w-5 text-white" />;
      case 'instagram':
        return <Instagram className="h-5 w-5 text-white" />;
      case 'twitter':
        return <Twitter className="h-5 w-5 text-white" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-white" />;
      case 'whatsapp':
        return <MessageCircle className="h-5 w-5 text-white" />;
      default:
        return <Globe className="h-5 w-5 text-white" />;
    }
  };

  const getSocialButtonStyle = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return 'bg-blue-600 hover:bg-blue-700 text-white';
      case 'instagram':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white';
      case 'twitter':
        return 'bg-blue-400 hover:bg-blue-500 text-white';
      case 'youtube':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'whatsapp':
        return 'bg-green-500 hover:bg-green-600 text-white';
      default:
        return 'bg-gray-600 hover:bg-gray-700 text-white';
    }
  };

  const contactItems = [
    {
      icon: PhoneCall,
      title: t('contact.phone'),
      value: BRAND.phone,
      link: `tel:${BRAND.phone}`,
      description: t('contact.work_hours')
    },
    {
      icon: Mail,
      title: "Email",
      value: BRAND.email,
      link: `mailto:${BRAND.email}`,
      description: t('contact.email_action')
    },
    {
      icon: MapPin,
      title: t('contact.address'),
      value: BRAND.address,
      link: null,
      description: t('contact.office_description')
    },
    {
      icon: Clock,
      title: t('contact.work_time'),
      value: t('contact.weekdays_hours'),
      link: null,
      description: t('contact.weekend_hours')
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок секции */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 text-dark-900">
            {t('contact.info.title')}
          </motion.h2>
          <motion.p variants={item} className="text-gray-600 text-lg max-w-3xl mx-auto">
            {t('contact.info.subtitle')}
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Левая колонка - контактная информация */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Основная контактная информация */}
            <motion.div variants={item} className="space-y-6">
              {contactItems.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-2xl hover:bg-gray-100 transition-all duration-200">
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                    <item.icon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-dark-900 mb-1">{item.title}</h3>
                    {item.link ? (
                      <a 
                        href={item.link}
                        className="text-rose-600 hover:text-rose-700 font-medium transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-gray-700 font-medium">{item.value}</p>
                    )}
                    <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Социальные сети */}
            <motion.div variants={item} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-dark-900 mb-4 flex items-center gap-3">
                <Globe className="h-5 w-5 text-rose-600" />
                {t('contact.social.title')}
              </h3>
                             <div className="flex flex-wrap gap-3">
                 {Object.entries(BRAND.social).map(([platform, url]) => (
                   <a
                     key={platform}
                     href={url}
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`p-3 rounded-xl transition-all duration-200 flex items-center justify-center ${getSocialButtonStyle(platform)}`}
                     title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                   >
                     {getSocialIcon(platform)}
                   </a>
                 ))}
               </div>
            </motion.div>
          </motion.div>

          {/* Правая колонка - Google карта */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Google карта */}
            <motion.div variants={item} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-dark-900 mb-2 flex items-center gap-3">
                  <MapPin className="h-6 w-6 text-rose-600" />
                  {t('contact.office')}
                </h3>
                <p className="text-gray-600">{BRAND.address}</p>
              </div>
              
              {/* Google Maps для Emmendingen */}
              <div className="relative h-96 w-full rounded-b-3xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2640.1234567890123!2d7.851234567890123!3d48.12345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDA3JzM0LjQiTiA3wrA1MScwNC40IkU!5e0!3m2!1sde!2sde!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Офис гипнотерапии в Германии"
                ></iframe>
              </div>
            </motion.div>

            {/* Информация о местоположении */}
            <motion.div variants={item} className="bg-gradient-to-br from-rose-600 to-rose-700 rounded-2xl p-6 border border-rose-500">
              <h3 className="font-semibold text-white mb-4">{t('contact.directions.title')}</h3>
                              <div className="space-y-3 text-sm text-rose-100">
                <p><strong className="text-white">{t('contact.directions.car')}:</strong> {t('contact.directions.car_description')}</p>
                <p><strong className="text-white">{t('contact.directions.public')}:</strong> {t('contact.directions.public_description')}</p>
                <p><strong className="text-white">{t('contact.directions.bike')}:</strong> {t('contact.directions.bike_description')}</p>
              </div>
            </motion.div>

            {/* Кнопка "Проложить маршрут" */}
            <motion.div variants={item}>
              <a
                href={`https://www.google.com/maps/dir//${encodeURIComponent(BRAND.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-4 px-6 rounded-2xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              >
                <MapPin className="h-5 w-5" />
                {t('contact.route_button')}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
